/**
 * Customer-segments admin page — orchestrates loading/error/detail states,
 * renders three tabs (canonical / RFM / featured), and exposes the region
 * filter synced to the URL via nuqs.
 *
 * All sub-components and constants live in ./Segments/*. This file should
 * only contain page-level composition; resist adding inline components here.
 */

import { useState } from "react";
import { PieChart, AlertCircle, Plus } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { useQueryState } from "nuqs";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { useSegments } from "@/hooks/admin/useCRM";
import type { CanonicalSegmentRow } from "@/lib/admin-api/crm";

import { TABS, REGIONS, type TabKey } from "./Segments/constants";
import SegmentCard from "./Segments/SegmentCard";
import RfmSegmentCard from "./Segments/RfmSegmentCard";
import SegmentDetail from "./Segments/SegmentDetail";
import CreateSegmentModal from "./Segments/CreateSegmentModal";
import FeaturedSegmentCard from "./Segments/FeaturedSegmentCard";

export default function Segments() {
  useAdminPageTitle("Segments");

  const [regionFilter, setRegionFilter] = useQueryState("region", {
    defaultValue: "",
  });
  const { data, isLoading, isError, refetch } = useSegments(
    regionFilter ? { region: regionFilter } : undefined,
  );
  const [activeTab, setActiveTab] = useState<TabKey>("canonical");
  const [activeSegment, setActiveSegment] =
    useState<CanonicalSegmentRow | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const totalCustomers = data?.total_customers ?? 0;

  const tabCounts: Record<TabKey, number> = {
    canonical: data?.canonical?.length ?? 0,
    rfm: data?.rfm?.length ?? 0,
    featured: data?.featured?.length ?? 0,
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-4 animate-pulse">
          <div className="h-8 w-56 rounded bg-stone-200" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-44 rounded-xl bg-stone-100" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-stone-300 bg-white p-16 text-center">
          <AlertCircle className="h-10 w-10 text-red-400" />
          <p className="text-sm font-medium text-stone-700">
            Impossible de charger les segments clients.
          </p>
          <Button onClick={() => void refetch()}>Réessayer</Button>
        </div>
      </AdminLayout>
    );
  }

  if (activeSegment) {
    return (
      <AdminLayout>
        <SegmentDetail
          segment={activeSegment}
          onBack={() => setActiveSegment(null)}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <PieChart className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Segments clients
              </h1>
              <p className="text-sm text-stone-500">
                Segmentation automatique —{" "}
                {totalCustomers.toLocaleString("fr-SN")} clients au total
              </p>
            </div>
          </div>
        </div>

        {/* Freshness banner when data comes from live query instead of cache */}
        {data?.source === "live" && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Données en temps réel — la vue matérialisée
            n&apos;est pas encore prête.
          </div>
        )}

        {/* Tabs + region filter row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            className="flex gap-1 rounded-lg bg-stone-100 p-1"
            role="tablist"
          >
            {TABS.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? "bg-white text-stone-900 shadow-sm"
                    : "text-stone-500 hover:text-stone-700"
                }`}
              >
                {tab.label} ({tabCounts[tab.key]})
              </button>
            ))}
          </div>

          <select
            value={regionFilter}
            onChange={(e) => void setRegionFilter(e.target.value || null)}
            aria-label="Filtre par région"
            className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-700"
          >
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {/* Tab content */}
        {activeTab === "canonical" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data?.canonical ?? []).map((seg) => (
              <SegmentCard
                key={seg.key}
                segment={seg}
                onClick={() => setActiveSegment(seg)}
              />
            ))}
            {(data?.canonical ?? []).length === 0 && (
              <div className="col-span-full text-center py-8 text-stone-500">
                <p className="text-sm">Aucun segment canonique trouvé.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "rfm" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data?.rfm ?? []).map((seg) => (
              <RfmSegmentCard key={seg.key} segment={seg} />
            ))}
            {(data?.rfm ?? []).length === 0 && (
              <div className="col-span-full text-center py-8 text-stone-500">
                <p className="text-sm">
                  Aucun segment RFM disponible pour le moment.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "featured" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-500">
                Segments personnalisés basés sur vos critères
              </p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Créer un segment
              </Button>
            </div>

            {(data?.featured ?? []).length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {(data?.featured ?? []).map((seg) => (
                  <FeaturedSegmentCard key={seg.id} segment={seg} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-stone-300 bg-white py-12 text-center">
                <Plus className="h-8 w-8 text-stone-300" />
                <p className="text-sm text-stone-500">
                  Aucun segment personnalisé. Créez votre premier segment
                  ciblé.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Créer un segment
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <CreateSegmentModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </AdminLayout>
  );
}
